/*
 * Pre-checkout del mini-cart de Peru Balloons (sin app). Adaptado del de Carolina Store.
 * «Continuar al pago» abre una hoja desde abajo con un solo paso: el distrito de entrega.
 *   1. Distritos, zona y región de cada uno desde distritos-envio.json (Lima Metropolitana y Callao).
 *   2. Al elegir el distrito, la Storefront API (sin token, sobre el mismo carrito de la tienda) fija
 *      la dirección y devuelve las tarifas que ofrecerá el checkout; se muestra la de su zona.
 *   3. Al enviar: «Distrito» y «Zona de envío» como atributos del pedido (cart/update.js), la
 *      dirección precargada y la tarifa de la zona preseleccionada; luego redirige al checkout
 *      (+ UTMs). Si algo falla, cae al checkout nativo.
 *      El checkout no respeta la tarifa preseleccionada (marca la primera). Lo que garantiza la
 *      tarifa correcta es la región: cada zona del JSON apunta a una región con su propia zona de
 *      envío en el admin, así el checkout ofrece solo esa tarifa.
 * Ojo: una dirección en una región sin zona de envío deja en 0 todas las líneas del carrito. Por eso
 * la región sale del JSON (Callao usa PE-LMA hasta que esté en la zona) y, si aun así el carrito
 * queda vacío, se restaura con cart/add.js.
 */
(() => {
  const API_VERSION = '2026-07';
  const COUNTRY = 'PE';
  // Región con zona de envío segura: la de Lima. Se usa para restaurar el carrito.
  const SAFE_REGION = 'PE-LMA';
  const STORAGE_KEY = 'enovaCheckoutDistrict';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];

  const ADDRESS_REPLACE = `
    mutation enovaDeliveryAddress($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
      cartDeliveryAddressesReplace(cartId: $cartId, addresses: $addresses) {
        cart {
          totalQuantity
          deliveryGroups(first: 10) {
            nodes { id deliveryOptions { handle title estimatedCost { amount } } }
          }
        }
        userErrors { code message }
      }
    }
  `;

  const SELECT_OPTIONS = `
    mutation enovaDeliveryOption($cartId: ID!, $options: [CartSelectedDeliveryOptionInput!]!) {
      cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $options) {
        cart { checkoutUrl }
        userErrors { code message }
      }
    }
  `;

  function cartEndpoint(path) {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return `${cartUrl}${path}`;
  }

  function checkoutFallbackUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}checkout`;
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
  }

  // Storefront API sin token: Shopify la abre para carritos desde el propio dominio de la tienda.
  function storefront(query, variables) {
    return fetch(`/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((response) => {
        if (response.errors && response.errors.length) throw new Error(response.errors[0].message);
        return response.data;
      });
  }

  function readSaved() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function writeSaved(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      /* sin storage: el flujo sigue igual */
    }
  }

  // El mini-cart puede abrirse en cualquier página: los UTMs de la visita se guardan al llegar.
  function captureUTMs() {
    try {
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) utms[key] = value;
      });
      if (Object.keys(utms).length) sessionStorage.setItem('utm_params', JSON.stringify(utms));
    } catch (error) {
      /* noop */
    }
  }

  function appendUTMs(url) {
    let utms = {};
    try {
      utms = JSON.parse(sessionStorage.getItem('utm_params')) || {};
    } catch (error) {
      utms = {};
    }
    const params = Object.keys(utms).map((key) => `${key}=${encodeURIComponent(utms[key])}`);
    if (!params.length) return url;
    return url + (url.includes('?') ? '&' : '?') + params.join('&');
  }

  function normalizeName(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function optionCents(option) {
    return Math.round((Number(option.estimatedCost && option.estimatedCost.amount) || 0) * 100);
  }

  // Tarifa de la zona entre las que devuelve Shopify: por el nombre que tiene en el admin y, si lo
  // cambiaron, la más barata para la zona cercana y la más cara para la extendida.
  function pickOption(options, zone, zoneKey) {
    if (!options || !options.length) return null;
    const wanted = normalizeName(zone.metodo);
    const byName = wanted && options.find((option) => normalizeName(option.title) === wanted);
    if (byName) return byName;
    const sorted = options.slice().sort((a, b) => optionCents(a) - optionCents(b));
    return zoneKey === 'extendida' ? sorted[sorted.length - 1] : sorted[0];
  }

  // Formato de moneda de la tienda (shop.money_format, p. ej. "S/ {{amount}}"). Recibe céntimos.
  function formatMoney(cents, format) {
    const value = (Number(cents) || 0) / 100;
    const group = (number, decimals, thousands, decimal) => {
      const [integer, fraction] = number.toFixed(decimals).split('.');
      const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
    };
    return (format || 'S/ {{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  class EnovaMinicartCheckout {
    constructor(drawer) {
      this.drawer = drawer;
      this.data = null;
      this.dataRequest = null;
      this.quoteRequest = 0;
      this.submitting = false;

      this.onKeyup = this.onKeyup.bind(this);
      drawer.addEventListener('click', this.onClick.bind(this), true);
      drawer.addEventListener('change', this.onChange.bind(this));
      drawer.addEventListener('submit', this.onSubmit.bind(this));

      // Al volver del checkout con «atrás» la página se restaura desde el bfcache tal cual se
      // dejó: con el botón en «Procesando…».
      window.addEventListener('pageshow', (event) => {
        if (event.persisted || this.submitting) {
          this.setSubmitting(false);
          const trigger = drawer.querySelector('[data-enova-minicart-checkout]');
          if (trigger) this.setTriggerBusy(trigger, false);
        }
      });

      // Si se cierra el drawer, la hoja se cierra con él. Al abrirse, el resumen toma el distrito guardado.
      new MutationObserver(() => {
        if (!drawer.classList.contains('active') && this.isOpen) this.close({ restoreFocus: false });
        if (drawer.classList.contains('active')) this.renderSummary();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      // El tema vuelve a dibujar el panel al cambiar el carrito (cantidad, quitar): el resumen se rehace.
      let summaryTimer = 0;
      new MutationObserver(() => {
        clearTimeout(summaryTimer);
        summaryTimer = setTimeout(() => this.renderSummary(), 30);
      }).observe(drawer, { childList: true, subtree: true });
    }

    // El tema re-renderiza el panel del drawer al cambiar el carrito: los elementos de la hoja se
    // buscan cada vez en lugar de guardarlos.
    get sheet() {
      return this.drawer.querySelector('[data-enova-checkout-sheet]');
    }

    part(name) {
      const sheet = this.sheet;
      return sheet ? sheet.querySelector(`[data-enova-checkout-${name}]`) : null;
    }

    get msg() {
      const sheet = this.sheet;
      return sheet ? sheet.dataset : {};
    }

    get isOpen() {
      const sheet = this.sheet;
      return !!sheet && sheet.classList.contains('is-open');
    }

    onClick(event) {
      // «cambiar» / «elegir distrito» del resumen: siempre abre la hoja de distritos
      const change = event.target.closest('[data-enova-checkout-change]');
      if (change && this.sheet) {
        event.preventDefault();
        this.open(change);
        return;
      }
      const trigger = event.target.closest('[data-enova-minicart-checkout]');
      if (trigger) {
        // Sin hoja en el DOM se deja pasar el envío del formulario: checkout nativo.
        if (trigger.disabled || !this.sheet) return;
        event.preventDefault();
        event.stopPropagation();
        // Con el distrito ya elegido (en la ficha o antes) «Ir a pagar» va directo al checkout; si no, la hoja lo pide.
        const saved = readSaved();
        if (!saved) return this.open(trigger);
        this.loadDistricts()
          .then(() => {
            const district = this.findDistrict(saved);
            if (!district) return this.open(trigger);
            this.setTriggerBusy(trigger, true);
            this.submitting = true;
            return this.goToCheckout(district);
          })
          .catch((error) => {
            console.warn('[enova-minicart] precheckout falló, se usa el checkout nativo', error);
            window.location.href = appendUTMs(checkoutFallbackUrl());
          });
        return;
      }
      if (event.target.closest('[data-enova-checkout-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    onChange(event) {
      // Selector del resumen: guarda el distrito, rehace el resumen y avisa a la ficha (si está abierta)
      if (event.target.matches('[data-cart-ship-select]')) {
        if (event.target.value) writeSaved(event.target.value);
        this.renderSummary();
        document.dispatchEvent(new CustomEvent('pb:district', { detail: { district: event.target.value } }));
        return;
      }
      if (!event.target.matches('[data-enova-checkout-district]')) return;
      this.clearError();
      if (event.target.value) writeSaved(event.target.value);
      this.quoteDistrict(event.target.value);
      this.renderSummary();
    }

    // En captura sobre document: Escape cierra solo la hoja, no el drawer entero.
    onKeyup(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    }

    // Capa de GPU para el panel solo mientras sube/baja.
    markSliding(sheet) {
      clearTimeout(this.slideTimer);
      sheet.classList.add('is-sliding');
      this.slideTimer = setTimeout(() => sheet.classList.remove('is-sliding'), 500);
    }

    // Anima la altura del panel cuando cambia su contenido (resumen, errores…). ResizeObserver
    // corre tras el layout y antes del pintado: se fija la altura anterior y en el siguiente frame
    // la nueva, así el salto nunca llega a verse. Una vez por cada hoja (el tema puede reemplazarla).
    watchHeight(sheet) {
      if (sheet.enovaHeightWatched || typeof ResizeObserver === 'undefined') return;
      const panel = sheet.querySelector('.enova-minicart-sheet__panel');
      const head = sheet.querySelector('.enova-minicart-sheet__head');
      const inner = sheet.querySelector('[data-enova-checkout-inner]');
      const actions = sheet.querySelector('[data-enova-checkout-actions]');
      if (!panel || !head || !inner || !actions) return;
      sheet.enovaHeightWatched = true;

      const naturalHeight = () => head.offsetHeight + inner.offsetHeight + actions.offsetHeight;
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
      let lastHeight = 0;
      let frame = 0;
      let timer = 0;

      const finish = (event) => {
        if (event && (event.target !== panel || event.propertyName !== 'height')) return;
        panel.style.height = '';
        panel.classList.remove('is-resizing');
      };
      panel.addEventListener('transitionend', finish);

      const observer = new ResizeObserver(() => {
        const height = naturalHeight();
        const previous = lastHeight;
        lastHeight = height;
        if (!previous || previous === height || !sheet.classList.contains('is-open')) return;
        if (reduceMotion && reduceMotion.matches) return;

        cancelAnimationFrame(frame);
        const from = panel.style.height ? panel.getBoundingClientRect().height : previous;
        panel.classList.add('is-resizing');
        panel.style.height = `${from}px`;
        frame = requestAnimationFrame(() => {
          panel.style.height = `${height}px`;
          // Si la altura queda topada por max-height no hay transitionend: se limpia igual.
          clearTimeout(timer);
          timer = setTimeout(finish, 450);
        });
      });
      [head, inner, actions].forEach((node) => observer.observe(node));
    }

    open(trigger) {
      const sheet = this.sheet;
      this.trigger = trigger;
      this.watchHeight(sheet);
      sheet.removeAttribute('inert');
      sheet.setAttribute('aria-hidden', 'false');
      this.markSliding(sheet);
      requestAnimationFrame(() => sheet.classList.add('is-open'));
      document.addEventListener('keyup', this.onKeyup, true);
      this.clearError();
      this.setSubmitting(false);
      // El carrito pudo cambiar: no se reutilizan montos de la vez anterior.
      this.lastCart = null;

      this.loadDistricts()
        .then(() => {
          const select = this.part('district');
          if (!select) return;
          this.renderDistricts(select);
          if (!select.value) {
            const saved = readSaved();
            if (saved && this.findDistrict(saved)) select.value = saved;
          }
          // El carrito pudo cambiar desde la última vez: la tarifa se recalcula al abrir.
          if (select.value) this.quoteDistrict(select.value);
          else this.resetShipping();
        })
        .catch(() => this.showError(this.msg.msgDistrictsError));

      setTimeout(() => {
        if (!this.isOpen) return;
        const panel = sheet.querySelector('.enova-minicart-sheet__panel');
        const select = this.part('district');
        const target = select && !select.disabled ? select : sheet.querySelector('[data-enova-checkout-close]');
        if (typeof window.trapFocus === 'function' && panel) window.trapFocus(panel, target);
        else if (target) target.focus({ preventScroll: true });
      }, 320);
    }

    close({ restoreFocus = true } = {}) {
      const sheet = this.sheet;
      document.removeEventListener('keyup', this.onKeyup, true);
      if (!sheet) return;
      this.markSliding(sheet);
      sheet.classList.remove('is-open');
      sheet.setAttribute('aria-hidden', 'true');
      sheet.setAttribute('inert', '');
      if (!restoreFocus) return;

      // El foco vuelve al drawer (el tema lo tiene atrapado ahí) y al botón que abrió la hoja.
      const container = document.getElementById('CartDrawer');
      const trigger =
        this.trigger && document.contains(this.trigger)
          ? this.trigger
          : this.drawer.querySelector('[data-enova-minicart-checkout]');
      if (typeof window.trapFocus === 'function' && container && trigger) window.trapFocus(container, trigger);
      else if (trigger) trigger.focus({ preventScroll: true });
    }

    /* ---------------- Distritos ---------------- */

    loadDistricts() {
      if (!this.dataRequest) {
        this.dataRequest = getJSON(this.sheet.dataset.districtsUrl)
          .then((data) => {
            this.data = data;
            return data;
          })
          .catch((error) => {
            this.dataRequest = null;
            throw error;
          });
      }
      return this.dataRequest;
    }

    // Un grupo por provincia (Lima Metropolitana, Callao), en orden alfabético.
    renderDistricts(select) {
      if (select.dataset.ready === 'true') return;
      const provinces = this.data.provincias || {};
      const groups = Object.keys(provinces)
        .map((key) => {
          const items = (this.data.distritos || [])
            .filter((district) => district.disponible !== false && district.provincia === key)
            .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
          if (!items.length) return '';
          const options = items
            .map((district) => `<option value="${escapeHTML(district.nombre)}">${escapeHTML(district.nombre)}</option>`)
            .join('');
          return `<optgroup label="${escapeHTML(provinces[key].nombre)}">${options}</optgroup>`;
        })
        .join('');
      select.innerHTML = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>${groups}`;
      select.disabled = false;
      select.dataset.ready = 'true';
    }

    findDistrict(name) {
      if (!this.data || !name) return null;
      return (this.data.distritos || []).find((district) => district.nombre === name && district.disponible !== false) || null;
    }

    zoneFor(district) {
      return (this.data.zonas && this.data.zonas[district.zona]) || { nombre: '', costo: 0 };
    }

    // Región de la dirección: la de la zona gana a la de la provincia. Con una zona de envío por
    // región en el admin, el checkout ofrece solo la tarifa del distrito (no respeta la preselección).
    regionFor(district) {
      const zone = this.zoneFor(district);
      const province = this.data.provincias && this.data.provincias[district.provincia];
      return zone.region_checkout || (province && province.region_checkout) || SAFE_REGION;
    }

    /* ---------------- Tarifa ---------------- */

    quoteDistrict(name) {
      const district = this.findDistrict(name);
      const request = ++this.quoteRequest;
      if (!district) {
        this.resetShipping();
        return;
      }
      this.showCalculating(district);

      this.requestQuote(district)
        .catch((error) => {
          console.warn('[enova-minicart] no se pudo leer la tarifa de Shopify', error);
          if (error.cartRestored) throw error;
          return this.fallbackQuote(district);
        })
        .then((quote) => {
          if (request !== this.quoteRequest) return;
          this.renderQuote(quote);
        })
        .catch(() => {
          if (request !== this.quoteRequest) return;
          this.resetShipping();
          this.showError(this.msg.msgUnavailable);
        });
    }

    // Fija la dirección del distrito en el carrito y lee las tarifas que ofrecerá el checkout.
    requestQuote(district) {
      return getJSON(cartEndpoint('.js')).then((cart) => {
        if (!cart.item_count) throw new Error('Carrito vacío');
        const cartId = `gid://shopify/Cart/${cart.token}`;
        return this.replaceAddress(cartId, district, this.regionFor(district)).then((result) => {
          // Región sin zona de envío: Shopify dejó las líneas en 0. Se devuelve el carrito como estaba.
          if (result.cart.totalQuantity === 0) {
            return this.restoreCart(cartId, district, cart).then(() => {
              const error = new Error(`La región ${this.regionFor(district)} no tiene zona de envío`);
              error.cartRestored = true;
              throw error;
            });
          }
          const zone = this.zoneFor(district);
          const groups = result.cart.deliveryGroups.nodes.map((group) => ({
            id: group.id,
            option: pickOption(group.deliveryOptions, zone, district.zona),
          }));
          if (!groups.length || groups.some((group) => !group.option)) throw new Error('Sin tarifas para el distrito');
          const shipping = groups.reduce((sum, group) => sum + optionCents(group.option), 0);
          return { district, zone, cart, cartId, groups, shipping, live: true };
        });
      });
    }

    // Sin respuesta de la Storefront API: tarifa de referencia del JSON (la de la política).
    fallbackQuote(district) {
      return getJSON(cartEndpoint('.js')).then((cart) => {
        const zone = this.zoneFor(district);
        return { district, zone, cart, groups: [], shipping: Math.round((Number(zone.costo) || 0) * 100), live: false };
      });
    }

    replaceAddress(cartId, district, region) {
      return storefront(ADDRESS_REPLACE, {
        cartId,
        addresses: [
          {
            selected: true,
            oneTimeUse: true,
            address: { deliveryAddress: { city: district.nombre, provinceCode: region, countryCode: COUNTRY } },
          },
        ],
      }).then((data) => {
        const result = data && data.cartDeliveryAddressesReplace;
        if (!result || !result.cart) throw new Error('cartDeliveryAddressesReplace sin carrito');
        if (result.userErrors.length) throw new Error(result.userErrors[0].message);
        return result;
      });
    }

    // Vuelve a poner las líneas (con sus propiedades: fecha, horario, dedicatoria…) tras dejar una
    // dirección en una región con envío. Van al revés porque Shopify pone arriba lo último que
    // entra: así queda el mismo orden y los índices de línea del drawer siguen valiendo.
    restoreCart(cartId, district, snapshot) {
      const items = snapshot.items
        .slice()
        .reverse()
        .map((item) => {
          const line = { id: item.variant_id, quantity: item.quantity, properties: item.properties || {} };
          if (item.selling_plan_allocation) line.selling_plan = item.selling_plan_allocation.selling_plan.id;
          return line;
        });
      return this.replaceAddress(cartId, district, SAFE_REGION).then(() => postJSON(cartEndpoint('/add.js'), { items }));
    }

    // Mientras llega la tarifa el resumen no desaparece: la zona del distrito se muestra al instante
    // y solo los montos que faltan (envío y total; la primera vez, también productos) van en skeleton.
    showCalculating(district) {
      this.renderSummary({ cart: this.lastCart, zone: this.zoneFor(district), shipping: null });
    }

    resetShipping() {
      this.quoteRequest += 1;
      const shipping = this.part('shipping');
      if (!shipping) return;
      shipping.hidden = true;
      shipping.innerHTML = '';
      shipping.removeAttribute('aria-busy');
    }

    renderQuote(quote) {
      this.lastCart = quote.cart;
      this.renderSummary({ cart: quote.cart, zone: quote.zone, shipping: quote.shipping });
    }

    // Productos (a precio original) − Descuentos + Envío de la zona = Total. Un monto en null se
    // pinta como skeleton.
    renderSummary({ cart, zone, shipping }) {
      const container = this.part('shipping');
      if (!container) return;
      const msg = this.msg;
      const format = msg.moneyFormat;
      const loading = shipping == null;
      const skeleton = (modifier = '') =>
        `<span class="enova-minicart-sheet__sk${modifier}" aria-label="${escapeHTML(msg.msgCalculating)}"></span>`;
      const live = (text) => `<span class="enova-minicart-sheet__live">${escapeHTML(text)}</span>`;

      const original = cart ? Number(cart.original_total_price) || 0 : null;
      const total = cart ? Number(cart.total_price) || 0 : null;
      const discount = cart ? Math.max(original - total, 0) : 0;
      const productsLabel = cart
        ? String(msg.msgProducts || '').replace('__COUNT__', cart.item_count)
        : String(msg.msgProducts || '').replace(/\s*\(__COUNT__\)/, '');
      const shippingLabel = [msg.msgShipping, zone.nombre].filter(Boolean).join(' · ');

      let html = `
        <div class="enova-minicart-sheet__line">
          <span>${escapeHTML(productsLabel)}</span>
          ${cart ? `<span>${escapeHTML(formatMoney(original, format))}</span>` : skeleton()}
        </div>`;
      if (discount > 0) {
        html += `
          <div class="enova-minicart-sheet__line enova-minicart-sheet__line--discount">
            <span>${escapeHTML(msg.msgDiscount)}</span>
            <span>−${escapeHTML(formatMoney(discount, format))}</span>
          </div>`;
      }
      html += `
        <div class="enova-minicart-sheet__line">
          <span>${escapeHTML(shippingLabel)}</span>
          ${loading ? skeleton() : live(shipping ? formatMoney(shipping, format) : msg.msgFree)}
        </div>
        <div class="enova-minicart-sheet__total">
          <span class="enova-minicart-sheet__total-label">${escapeHTML(msg.msgTotal)}</span>
          ${loading || !cart ? skeleton(' enova-minicart-sheet__sk--total') : live(formatMoney(total + shipping, format))}
        </div>`;

      container.hidden = false;
      container.setAttribute('aria-busy', loading ? 'true' : 'false');
      container.innerHTML = html;
    }

    /* ---------------- Envío del formulario ---------------- */

    showError(message) {
      const error = this.part('error');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    clearError() {
      const error = this.part('error');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      const select = this.part('district');
      if (select) select.classList.remove('has-error');
    }

    setTriggerBusy(trigger, busy) {
      const label = trigger.querySelector('[data-checkout-label]');
      trigger.disabled = busy;
      trigger.classList.toggle('is-loading', busy);
      trigger.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (!label) return;
      if (!label.dataset.defaultLabel) label.dataset.defaultLabel = label.textContent;
      label.textContent = busy ? this.msg.msgProcessing || 'Procesando…' : label.dataset.defaultLabel;
    }

    // Resumen del carrito: «Envío a Miraflores · cambiar  S/ 20» y el total en el botón, con el distrito guardado;
    // sin distrito, «Envío · elegir distrito  desde S/ 20».
    renderSummary() {
      const row = this.drawer.querySelector('[data-cart-ship]');
      if (!row || !this.sheet) return;
      this.loadDistricts()
        .then(() => {
          const select = row.querySelector('[data-cart-ship-select]');
          const name = row.querySelector('[data-cart-ship-name]');
          const cost = row.querySelector('[data-cart-ship-cost]');
          const total = this.drawer.querySelector('[data-cart-total]');
          const subtotal = Number(row.dataset.subtotal) || 0;
          const money = (amount) => `S/ ${Math.round(amount)}`;
          // Solo se escribe lo que cambia: el resumen vive dentro del panel que se observa (si no, se redibujaría sin fin)
          const put = (el, text) => { if (el && el.textContent !== text) el.textContent = text; };
          if (select && select.dataset.ready !== 'true') {
            this.renderDistricts(select);
            select.options[0].textContent = 'tu distrito';
          }
          const district = this.findDistrict(readSaved());
          if (select && select.value !== (district ? district.nombre : '')) select.value = district ? district.nombre : '';
          put(name, district ? district.nombre : 'tu distrito');
          if (district) {
            put(cost, money(district.costo));
            put(total, money(subtotal / 100 + Number(district.costo)));
          } else {
            const min = Math.min(...(this.data.distritos || []).map((d) => Number(d.costo) || 0).filter(Boolean));
            put(cost, Number.isFinite(min) ? `desde ${money(min)}` : '');
            put(total, money(subtotal / 100));
          }
        })
        .catch(() => {});
    }

    setSubmitting(submitting) {
      this.submitting = submitting;
      const button = this.part('submit');
      const label = this.part('submit-label');
      if (!button || !label) return;
      button.disabled = submitting;
      button.classList.toggle('is-loading', submitting);
      button.setAttribute('aria-busy', submitting ? 'true' : 'false');
      if (!label.dataset.defaultLabel) label.dataset.defaultLabel = label.textContent;
      label.textContent = submitting ? this.msg.msgProcessing : label.dataset.defaultLabel;
    }

    onSubmit(event) {
      if (!event.target.matches('[data-enova-checkout-form]')) return;
      event.preventDefault();
      if (this.submitting) return;
      this.clearError();

      const select = this.part('district');
      const district = select && this.findDistrict(select.value);
      if (!district) {
        if (select) select.classList.add('has-error');
        this.showError(this.msg.msgRequired);
        return;
      }

      this.setSubmitting(true);
      this.goToCheckout(district).catch((error) => {
        console.warn('[enova-minicart] precheckout falló, se usa el checkout nativo', error);
        window.location.href = appendUTMs(checkoutFallbackUrl());
      });
    }

    // Todo se recalcula al enviar: el carrito pudo cambiar desde que se mostró la tarifa.
    goToCheckout(district) {
      writeSaved(district.nombre);
      const zone = this.zoneFor(district);
      const attributes = { Distrito: district.nombre, 'Zona de envío': zone.nombre };

      return postJSON(cartEndpoint('/update.js'), { attributes })
        .then(() => this.requestQuote(district))
        .then((quote) =>
          storefront(SELECT_OPTIONS, {
            cartId: quote.cartId,
            options: quote.groups.map((group) => ({ deliveryGroupId: group.id, deliveryOptionHandle: group.option.handle })),
          })
        )
        .then((data) => {
          const result = data && data.cartSelectedDeliveryOptionsUpdate;
          if (!result || !result.cart || result.userErrors.length) {
            throw new Error((result && result.userErrors[0] && result.userErrors[0].message) || 'Sin checkoutUrl');
          }
          window.location.href = appendUTMs(result.cart.checkoutUrl);
        });
    }
  }

  // /cart redirige al inicio con ?cart=open (theme.liquid): aquí se abre el drawer y se limpia la URL.
  function openFromURL(drawer) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cart') !== 'open') return;
    params.delete('cart');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    customElements.whenDefined('cart-drawer').then(() => drawer.open());
  }

  function init() {
    captureUTMs();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.enovaCheckoutReady === 'true') return;
    drawer.dataset.enovaCheckoutReady = 'true';
    new EnovaMinicartCheckout(drawer);
    openFromURL(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
